import { FC, useCallback } from 'react';
import { useField } from 'formik';
import graphql from 'babel-plugin-relay/macro';

import MenuItem from '@mui/material/MenuItem';
import ListItemText from '@mui/material/ListItemText';
import Typography from '@mui/material/Typography';
import AsyncSelect, { AsyncSelectProps } from 'components/AsyncSelect';

import {
  HotelSearchSelect_data$data,
  HotelSearchSelect_data$key,
} from '__generated__/HotelSearchSelect_data.graphql';
import Query, {
  HotelSearchSelectRefetchQuery,
} from '__generated__/HotelSearchSelectRefetchQuery.graphql';

export type Option = {
  readonly name: string;
  readonly internalId: string | null;
  readonly externalId: string | null;
};

type SelectProps = Omit<
  AsyncSelectProps<Option>,
  'fragment' | 'getOptions' | 'query'
>;

type Props = SelectProps & {
  data: HotelSearchSelect_data$key;
  name: string;
};

const getOptions = (data: HotelSearchSelect_data$data) =>
  data.hotelSearch ?? [];

const getOptionLabel = ({ name }: Option) => name;

const optionEqual = (option: Option, value: Option) =>
  Boolean(
    (option.internalId && option.internalId === value.internalId) ||
      (option.externalId && option.externalId === value.externalId)
  );

const renderOption = (props: any, { name, externalId }: Option) => (
  <MenuItem {...props}>
    <ListItemText>{name}</ListItemText>

    <Typography variant="body2" color="text.secondary">
      {externalId ? '[ETG]' : '[TEDO]'}
    </Typography>
  </MenuItem>
);

const getVariables = (search: string) => ({
  search,
  searching: !!search,
});

const HotelSearchSelect: FC<Props> = ({ name, helperText, ...props }) => {
  const [{ value }, meta, { setValue }] = useField(name);
  const hasError = Boolean(meta.touched && meta.error);

  const handleChange = useCallback(
    (_, value) => {
      setValue(value);
    },
    [setValue]
  );

  return (
    <AsyncSelect<Option, HotelSearchSelectRefetchQuery>
      query={Query}
      renderOption={renderOption}
      getOptionLabel={getOptionLabel}
      isOptionEqualToValue={optionEqual}
      getOptions={getOptions}
      value={value}
      onChange={handleChange}
      error={hasError}
      helperText={hasError ? meta.error : helperText}
      getVariables={getVariables}
      fragment={graphql`
        fragment HotelSearchSelect_data on Query
        @argumentDefinitions(
          search: { type: "String" }
          searching: { type: "Boolean", defaultValue: false }
        )
        @refetchable(queryName: "HotelSearchSelectRefetchQuery") {
          hotelSearch(search: $search) @include(if: $searching) {
            name
            internalId
            externalId
          }
        }
      `}
      {...props}
    />
  );
};

export default HotelSearchSelect;
